import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { filter, startWith, take } from 'rxjs/operators';

import { SessionService } from '../core/session.service';
import { Analytics, EVENT } from './analytics';
import { FeatureFlagsService } from './feature-flags.service';

/**
 * One number for every message the employee reads (DEV-1999). The flag is an
 * allow-list of company ids, so a per-company value would need a second flag.
 */
export const EMPLOYEE_MESSAGE_MAX_LENGTH = 400;

/** Where a message is written, for the rollout signal. */
export type EmployeeMessageSurface =
  | 'refusal_dialog'
  | 'approval_dialog'
  | 'edit_form'
  | 'template';

/**
 * Angular's own `maxlength` error, so `actualLength` and `requiredLength` feed
 * the counter and the error text from the same object. Without a limit it is
 * a validator that passes everything, not `null`: a `null` inside a validator
 * array is read as a `Validator` instance and throws.
 */
export const employeeMessageLimitValidator = (
  limit: number | null
): ValidatorFn => (limit === null ? () => null : Validators.maxLength(limit));

/**
 * The company-scoped length limit on the messages an operator writes for the
 * employee: refusal, approval and their templates. Only the screens enforce
 * it -- the backend accepts ~4000 characters and the automatic refusals write
 * their own text.
 *
 * Each screen reads `limit` once, when it builds its form, and derives both
 * its counter and its validator from that one number. A form that outlives a
 * company switch keeps the limit of the company it was built for; the dialogs
 * and the edit modal are rebuilt per use.
 */
@Injectable({ providedIn: 'root' })
export class EmployeeMessageLimitService {
  constructor(
    private featureFlagsService: FeatureFlagsService,
    private sessionService: SessionService,
    private analytics: Analytics
  ) {}

  /** `null` when the current company is off the rollout. */
  get limit(): number | null {
    return this.featureFlagsService.isEnabledForCompany(
      'employeeMessageLimit',
      this.sessionService.currentCompanyId
    )
      ? EMPLOYEE_MESSAGE_MAX_LENGTH
      : null;
  }

  /**
   * Reports the first time the control's message is over the limit, once per
   * form: the rollout signal is "how often the cap bites", not keystrokes.
   * The current status is checked as well, for a message that arrives over
   * the limit before anything changes (the one on file, a template on open).
   */
  track(
    control: AbstractControl,
    surface: EmployeeMessageSurface
  ): Subscription {
    return control.statusChanges
      .pipe(
        startWith(control.status),
        filter(() => control.hasError('maxlength')),
        take(1)
      )
      .subscribe(() => {
        const { actualLength, requiredLength } = control.getError('maxlength');

        this.analytics.event(EVENT.EMPLOYEE_MESSAGE_OVER_LIMIT, {
          surface,
          length: actualLength,
          limit: requiredLength,
        });
      });
  }
}
